import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import useStore from '../../store';
import { usersApi } from '../../services/api';
import './TeamOverview.css';

/**
 * Builds a rolled-up tree from the flat reportees list.
 * Nodes whose manager isn't present in the list (i.e. direct reports of
 * the viewing manager) become roots; everyone else nests under their
 * manager's node. Runs in O(n) using a single map pass.
 */
function buildTree(users, rootManagerId) {
  const byId = new Map(users.map((u) => [u.id, { ...u, children: [] }]));
  const roots = [];

  byId.forEach((node) => {
    const parentId = node.manager?.id;
    if (parentId && parentId !== rootManagerId && byId.has(parentId)) {
      byId.get(parentId).children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortByName = (a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
  const sortRecursive = (nodes) => {
    nodes.sort(sortByName);
    nodes.forEach((n) => sortRecursive(n.children));
  };
  sortRecursive(roots);

  // Precompute each node's total descendant count once (post-order) so render doesn't recount it.
  const computeDescendantCount = (node) => {
    node.descendantCount = node.children.reduce((sum, child) => sum + 1 + computeDescendantCount(child), 0);
    return node.descendantCount;
  };
  roots.forEach(computeDescendantCount);

  return roots;
}

// Collects ids of every node that has children (leaf nodes never need an expanded-state entry).
function collectExpandableIds(nodes, acc = []) {
  nodes.forEach((node) => {
    if (node.children.length > 0) {
      acc.push(node.id);
      collectExpandableIds(node.children, acc);
    }
  });
  return acc;
}

const TeamNode = memo(function TeamNode({ node, depth, expandedIds, onToggle }) {
  const hasChildren = node.children.length > 0;
  const isExpanded = expandedIds.has(node.id);

  return (
    <div className="team-node">
      <div className="team-node-row" style={{ paddingLeft: depth * 24 }}>
        <button
          type="button"
          className={`team-node-toggle ${hasChildren ? '' : 'team-node-toggle--empty'}`}
          onClick={() => hasChildren && onToggle(node.id)}
          aria-label={hasChildren ? (isExpanded ? 'Collapse' : 'Expand') : undefined}
          disabled={!hasChildren}
        >
          {hasChildren ? (
            <svg
              className={`team-node-chevron ${isExpanded ? 'team-node-chevron--expanded' : ''}`}
              width="14" height="14" viewBox="0 0 16 16" fill="none"
            >
              <path d="M5 3.5L10.5 8L5 12.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            <span className="team-node-leaf-dot" />
          )}
        </button>

        <div className="avatar avatar-sm">{node.first_name?.[0]}{node.last_name?.[0]}</div>

        <div className="team-node-info">
          <span className="team-node-name">{node.first_name} {node.last_name}</span>
          <span className="team-node-meta">{node.designation || '—'} · {node.department?.name || '—'}</span>
        </div>

        <span className="badge badge-violet">{node.role}</span>
        {hasChildren && (
          <span className="badge badge-gray">{node.descendantCount} report{node.descendantCount === 1 ? '' : 's'}</span>
        )}
        <span className="team-node-email">{node.email}</span>
      </div>

      {hasChildren && isExpanded && (
        <div className="team-node-children">
          {node.children.map((child) => (
            <TeamNode key={child.id} node={child} depth={depth + 1} expandedIds={expandedIds} onToggle={onToggle} />
          ))}
        </div>
      )}
    </div>
  );
});

export default function TeamOverviewPage() {
  const { user } = useStore();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('rolled-up'); // 'rolled-up' | 'flat'
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  useEffect(() => {
    if (!user?.id) return;
    setLoading(true);
    usersApi.getReportees(user.id)
      .then((res) => setUsers(res.data.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [user?.id]);

  const tree = useMemo(() => buildTree(users, user?.id), [users, user?.id]);

  // Tree starts fully collapsed; users expand branches they care about.
  useEffect(() => {
    setExpandedIds(new Set());
  }, [tree]);

  const handleToggle = useCallback((id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const expandableIds = useMemo(() => collectExpandableIds(tree), [tree]);
  const handleExpandAll = useCallback(() => setExpandedIds(new Set(expandableIds)), [expandableIds]);
  const handleCollapseAll = useCallback(() => setExpandedIds(new Set()), []);

  const directReportsCount = tree.length;
  const totalTeamCount = users.length;

  if (loading) {
    return <div className="team-loading">Loading team…</div>;
  }

  return (
    <div className="team-overview-page">
      <div className="page-header">
        <div>
          <div className="page-title">Team Overview</div>
          <div className="page-subtitle">Your direct and indirect reports</div>
        </div>
      </div>

      <div className="team-summary-bar">
        <div className="team-summary-stat">
          <span className="team-summary-value">{directReportsCount}</span>
          <span className="team-summary-label">Direct reports</span>
        </div>
        <div className="team-summary-stat">
          <span className="team-summary-value">{totalTeamCount}</span>
          <span className="team-summary-label">Total team size</span>
        </div>
      </div>

      <div className="team-controls-bar">
        {view === 'rolled-up' && expandableIds.length > 0 ? (
          <div className="team-expand-controls">
            <button
              type="button"
              className="team-tree-icon-btn"
              onClick={handleExpandAll}
              disabled={expandedIds.size === expandableIds.length}
              title="Expand all"
              aria-label="Expand all"
            >
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                <path d="M2 6V2h4M14 6V2h-4M2 10v4h4M14 10v4h-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button
              type="button"
              className="team-tree-icon-btn"
              onClick={handleCollapseAll}
              disabled={expandedIds.size === 0}
              title="Collapse all"
              aria-label="Collapse all"
            >
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                <path d="M6 2v4H2M10 2v4h4M6 14v-4H2M10 14v-4h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        ) : <span />}

        <button
          type="button"
          className="team-view-switch"
          role="switch"
          aria-checked={view === 'flat'}
          onClick={() => setView(view === 'rolled-up' ? 'flat' : 'rolled-up')}
        >
          <span className={`team-view-switch-option ${view === 'rolled-up' ? 'team-view-switch-option--active' : ''}`}>Rolled-up</span>
          <span className={`team-view-switch-option ${view === 'flat' ? 'team-view-switch-option--active' : ''}`}>Flat list</span>
          <span className={`team-view-switch-thumb ${view === 'flat' ? 'team-view-switch-thumb--right' : ''}`} />
        </button>
      </div>

      {users.length === 0 ? (
        <div className="card empty-state">
          <div className="empty-state-icon">👥</div>
          <div className="empty-state-title">No reportees found</div>
        </div>
      ) : view === 'rolled-up' ? (
        <div className="card team-tree">
          {tree.map((root) => (
            <TeamNode key={root.id} node={root} depth={0} expandedIds={expandedIds} onToggle={handleToggle} />
          ))}
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrapper" style={{ border: 'none' }}>
            <table>
              <thead>
                <tr>
                  <th>Name</th><th>Role</th><th>Designation</th>
                  <th>Department</th><th>Email</th><th>Manager</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div className="avatar avatar-sm">{u.first_name?.[0]}{u.last_name?.[0]}</div>
                        <span style={{ fontWeight: 600 }}>{u.first_name} {u.last_name}</span>
                      </div>
                    </td>
                    <td><span className="badge badge-violet">{u.role}</span></td>
                    <td style={{ fontSize: '0.875rem' }}>{u.designation || '—'}</td>
                    <td style={{ fontSize: '0.875rem' }}>{u.department?.name || '—'}</td>
                    <td style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>{u.email}</td>
                    <td style={{ fontSize: '0.875rem' }}>
                      {u.manager ? `${u.manager.first_name} ${u.manager.last_name}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
